import { ILaya } from "../../ILaya";
import { NodeFlags } from "../Const";
import { GrahamScan } from "../maths/GrahamScan";
import { Matrix } from "../maths/Matrix";
import { Point } from "../maths/Point";
import { Rectangle } from "../maths/Rectangle";
import { RenderSprite } from "../renders/RenderSprite";
import { Context } from "../renders/Context";
import { HTMLCanvas } from "../resource/HTMLCanvas";
import { Texture } from "../resource/Texture";
import { CacheStyle } from "./css/CacheStyle";
import { Graphics } from "./Graphics";
import { Node } from "./Node";
import { SpriteConst, TransformKind } from "./SpriteConst";
import { RenderTexture2D } from "../resource/RenderTexture2D";
import { Event } from "../events/Event";
import { DragSupport } from "../utils/DragSupport";
import { URL } from "../net/URL";
import { LayaEnv } from "../../LayaEnv";
import { SpriteUtils } from "../utils/SpriteUtils";
import { RenderTargetFormat } from "../RenderEngine/RenderEnum/RenderTargetFormat";
import { BaseRenderNode2D } from "../NodeRender2D/BaseRenderNode2D";
import { SpriteGlobalTransform } from "./SpriteGlobaTransform";
const hiddenBits = NodeFlags.FORCE_HIDDEN | NodeFlags.NOT_IN_PAGE;
export class Sprite extends Node {
    constructor() {
        super();
        this._x = 0;
        this._y = 0;
        this._width = 0;
        this._height = 0;
        this._scaleX = 1;
        this._scaleY = 1;
        this._skewX = 0;
        this._skewY = 0;
        this._pivotX = 0;
        this._pivotY = 0;
        this._anchorX = 0;
        this._anchorY = 0;
        this._rotation = 0;
        this._alpha = 1;
        this._visible = true;
        this._mouseState = 0;
        this._zOrder = 0;
        this._renderType = 0;
        this._cacheStyle = CacheStyle.EMPTY;
        this.mouseThrough = false;
        this.hitTestPrior = false;
        this._autosize = false;
        this._repaint = 0;
        this._sizeFlag = 0;
    }
    destroy(destroyChild = true) {
        super.destroy(destroyChild);
        this._cacheStyle && this._cacheStyle.recover();
        this._cacheStyle = null;
        this._texture && this._texture._removeReference();
        this._texture = null;
        this._graphics && this._ownGraphics && this._graphics.destroy();
        this._graphics = null;
    }
    get parent() {
        return this._$parent;
    }
    get scene() {
        return this._scene;
    }
    get x() {
        return this._x;
    }
    set x(value) {
        this.pos(value, this._y);
    }
    get y() {
        return this._y;
    }
    set y(value) {
        this.pos(this._x, value);
    }
    get width() {
        if (this._autosize)
            return this.getSelfBounds(tmpRect).width;
        else if ((this._sizeFlag & 1) == 0)
            return this.measureWidth();
        else
            return this._width;
    }
    set width(value) {
        this.size(value, (this._sizeFlag & 2) == 0 ? null : this._height);
    }
    get height() {
        if (this._autosize)
            return this.getSelfBounds(tmpRect).height;
        else if ((this._sizeFlag & 2) == 0)
            return this.measureHeight();
        else
            return this._height;
    }
    set height(value) {
        this.size((this._sizeFlag & 1) == 0 ? null : this._width, value);
    }
    get _isWidthSet() {
        return (this._sizeFlag & 1) != 0;
    }
    get _isHeightSet() {
        return (this._sizeFlag & 2) != 0;
    }
    measureWidth() {
        return this._texture ? this._texture.width : 0;
    }
    measureHeight() {
        return this._texture ? this._texture.height : 0;
    }
    get displayWidth() {
        return this.width * this.scaleX;
    }
    get displayHeight() {
        return this.height * this.scaleY;
    }
    get scaleX() {
        return this._scaleX;
    }
    set scaleX(value) {
        this.scale(value, this._scaleY);
    }
    get scaleY() {
        return this._scaleY;
    }
    set scaleY(value) {
        this.scale(this._scaleX, value);
    }
    get rotation() {
        return this._rotation;
    }
    set rotation(value) {
        if (this._rotation !== value) {
            this._rotation = value;
            this._transChanged(TransformKind.Rotation);
        }
    }
    get skewX() {
        return this._skewX;
    }
    set skewX(value) {
        this.skew(value, this._skewY);
    }
    get skewY() {
        return this._skewY;
    }
    set skewY(value) {
        this.skew(this._skewX, value);
    }
    get transform() {
        if (!this._tfChanged)
            return this._transform;
        this._tfChanged = false;
        let m = this._transform || (this._transform = new Matrix());
        let sx = this._scaleX, sy = this._scaleY;
        let sskx = this._skewX;
        let ssky = this._skewY;
        let rot = this._rotation;
        if (rot || sx !== 1 || sy !== 1 || sskx !== 0 || ssky !== 0) {
            m._bTransform = true;
            let skx = (rot - sskx) * 0.0174532922222222;
            let sky = (rot + ssky) * 0.0174532922222222;
            let cx = Math.cos(sky);
            let ssx = Math.sin(sky);
            let cy = Math.sin(skx);
            let ssy = Math.cos(skx);
            m.a = sx * cx;
            m.b = sx * ssx;
            m.c = -sy * cy;
            m.d = sy * ssy;
            m.tx = m.ty = 0;
        }
        else {
            m.identity();
            this._renderType &= ~SpriteConst.TRANSFORM;
        }
        return m;
    }
    set transform(value) {
        this._tfChanged = false;
        let m = this._transform || (this._transform = new Matrix());
        if (value !== m)
            value.copyTo(m);
        if (value) {
            this._x = m.tx;
            this._y = m.ty;
            m.tx = m.ty = 0;
        }
        this._renderType |= SpriteConst.TRANSFORM;
        this.parentRepaint();
    }
    get globalTrans() {
        return this._globalTrans || (this._globalTrans = new SpriteGlobalTransform(this));
    }
    get pivotX() {
        return this._pivotX;
    }
    set pivotX(value) {
        this.pivot(value, this._pivotY);
    }
    get pivotY() {
        return this._pivotY;
    }
    set pivotY(value) {
        this.pivot(this._pivotX, value);
    }
    get anchorX() {
        return this._anchorX;
    }
    set anchorX(value) {
        this.anchor(value, this._anchorY);
    }
    get anchorY() {
        return this._anchorY;
    }
    set anchorY(value) {
        this.anchor(this._anchorX, value);
    }
    get alpha() {
        return this._alpha;
    }
    set alpha(value) {
        value = value < 0 ? 0 : (value > 1 ? 1 : value);
        if (this._alpha !== value) {
            this._alpha = value;
            if (value !== 1)
                this._renderType |= SpriteConst.ALPHA;
            else
                this._renderType &= ~SpriteConst.ALPHA;
            this.parentRepaint();
        }
    }
    get visible() {
        return this._visible;
    }
    set visible(value) {
        if (this._visible !== value) {
            this._visible = value;
            this._processVisible();
        }
    }
    get blendMode() {
        return this._blendMode;
    }
    set blendMode(value) {
        if (this._blendMode != value) {
            this._blendMode = value;
            if (value && value != "source-over")
                this._renderType |= SpriteConst.BLEND;
            else
                this._renderType &= ~SpriteConst.BLEND;
            this.parentRepaint();
        }
    }
    get graphics() {
        if (!this._graphics) {
            this.graphics = new Graphics();
            this._ownGraphics = true;
        }
        return this._graphics;
    }
    set graphics(value) {
        this.setGraphics(value, false);
    }
    setGraphics(value, transferOwnership) {
        if (this._graphics) {
            this._graphics._sp = null;
            if (this._ownGraphics)
                this._graphics.destroy();
        }
        this._ownGraphics = transferOwnership;
        this._graphics = value;
        if (value) {
            this._renderType |= SpriteConst.GRAPHICS;
            value._sp = this;
        }
        else {
            this._renderType &= ~SpriteConst.GRAPHICS;
        }
        this.repaint();
    }
    get filters() {
        return this._filterArr;
    }
    set filters(value) {
        value && value.length === 0 && (value = null);
        if (this._filterArr) {
            for (let f of this._filterArr) {
                f && f.off(Event.CHANGED, this, this.repaint);
            }
        }
        this._filterArr = value ? value.slice() : null;
        if (value) {
            for (let f of value) {
                f && f.on(Event.CHANGED, this, this.repaint);
            }
        }
        if (value)
            this._renderType |= SpriteConst.FILTERS;
        else
            this._renderType &= ~SpriteConst.FILTERS;
        if (value && value.length > 0) {
            if (!this._getBit(NodeFlags.DISPLAY))
                this._setBitUp(NodeFlags.DISPLAY);
        }
        this.repaint();
    }
    get cacheAs() {
        return this._getCacheStyle().userSetCache;
    }
    set cacheAs(value) {
        if (value === this._cacheStyle.userSetCache)
            return;
        this._getCacheStyle().userSetCache = value;
        if (this.mask && value === 'normal')
            return;
        if (value == 'bitmap' || value == 'normal') {
            this._renderType |= SpriteConst.CANVAS;
        }
        else {
            this._renderType &= ~SpriteConst.CANVAS;
        }
        this.repaint();
    }
    get staticCache() {
        return this._getCacheStyle().staticCache;
    }
    set staticCache(value) {
        this._getCacheStyle().staticCache = value;
        if (!value)
            this.reCache();
    }
    get mask() {
        return this._cacheStyle.mask;
    }
    set mask(value) {
        if (value == this || (value && this.mask == value && value._cacheStyle.maskParent == this))
            return;
        if (this.mask)
            this.mask._getCacheStyle().maskParent = null;
        this._getCacheStyle().mask = value;
        if (value) {
            value._getCacheStyle().maskParent = this;
            this._renderType |= SpriteConst.MASK;
        }
        else
            this._renderType &= ~SpriteConst.MASK;
        this.repaint();
    }
    get scrollRect() {
        return this._scrollRect;
    }
    set scrollRect(value) {
        if (this._scrollRect == null && value == null)
            return;
        this._scrollRect = value;
        if (value) {
            this._renderType |= SpriteConst.CLIP;
        }
        else {
            this._renderType &= ~SpriteConst.CLIP;
        }
        this.repaint();
    }
    get viewport() {
        return this._viewport;
    }
    set viewport(value) {
        if (typeof (value) == 'string') {
            let recArr = value.split(",");
            if (recArr.length > 3) {
                value = new Rectangle(parseFloat(recArr[0]), parseFloat(recArr[1]), parseFloat(recArr[2]), parseFloat(recArr[3]));
            }
        }
        this._viewport = value;
    }
    set drawCallOptimize(value) {
        this._setBit(NodeFlags.DRAWCALL_OPTIMIZE, value);
    }
    get drawCallOptimize() {
        return this._getBit(NodeFlags.DRAWCALL_OPTIMIZE);
    }
    get hitArea() {
        return this._hitArea;
    }
    set hitArea(value) {
        this._hitArea = value;
    }
    get mouseEnabled() {
        return this._mouseState === 2;
    }
    set mouseEnabled(value) {
        let i = value ? 2 : 1;
        if (this._mouseState !== i) {
            this._mouseState = i;
            if (i === 2)
                this.setMouseEnabledUp();
        }
    }
    setMouseEnabledUp() {
        let p = this._parent;
        while (p && p !== ILaya.stage) {
            if (p._mouseState !== 0 || !p._setBit(NodeFlags.CHECK_INPUT, true))
                break;
            p = p._parent;
        }
    }
    getMousePoint() {
        return this.globalToLocal(tmpPoint.setTo(ILaya.stage.mouseX, ILaya.stage.mouseY));
    }
    get mouseX() {
        return this.getMousePoint().x;
    }
    get mouseY() {
        return this.getMousePoint().y;
    }
    get globalScaleX() {
        return this.globalTrans.scaleX;
    }
    get globalScaleY() {
        return this.globalTrans.scaleY;
    }
    get zOrder() {
        return this._zOrder;
    }
    set zOrder(value) {
        if (this._zOrder != value) {
            this._zOrder = value;
            if (this._parent) {
                value && this._parent._setBit(NodeFlags.HAS_ZORDER, true);
                ILaya.systemTimer.callLater(this._parent, this.updateZOrder);
            }
        }
    }
    updateZOrder() {
        SpriteUtils.updateOrder(this._children) && this.repaint();
    }
    get texture() {
        return this._texture;
    }
    set texture(value) {
        if (this._texture == value)
            return;
        this._texture && this._texture._removeReference();
        this._texture = value;
        if (value) {
            value._addReference();
            this._renderType |= SpriteConst.TEXTURE;
        }
        else
            this._renderType &= ~SpriteConst.TEXTURE;
        this.repaint();
    }
    get material() {
        var _a;
        return (_a = this._graphics) === null || _a === void 0 ? void 0 : _a.material;
    }
    set material(value) {
        if (this._graphics == null && value == null)
            return;
        this.graphics.material = value;
    }
    get renderNode2D() {
        return this._renderNode;
    }
    set renderNode2D(value) {
        this._renderNode = value;
        if (value) {
            this._renderType |= SpriteConst.RENDERNODE2D;
        }
        else {
            this._renderType &= ~SpriteConst.RENDERNODE2D;
        }
    }
    set customRenderEnable(b) {
        if (b) {
            this._renderType |= SpriteConst.CUSTOM;
        }
    }
    get autoSize() {
        return this._autosize;
    }
    set autoSize(value) {
        this._autosize = value;
    }
    pos(x, y) {
        if (this._x != x || this._y != y) {
            this._x = x;
            this._y = y;
            this._transChanged(TransformKind.Pos);
        }
        return this;
    }
    pivot(x, y) {
        if (this._pivotX != x || this._pivotY != y) {
            this._pivotX = x;
            this._pivotY = y;
            let t = this.width;
            if (t != 0)
                this._anchorX = x / t;
            t = this.height;
            if (t != 0)
                this._anchorY = y / t;
            this._transChanged(TransformKind.Anchor);
        }
        return this;
    }
    anchor(x, y) {
        if (this._anchorX != x || this._anchorY != y) {
            this._anchorX = x;
            this._anchorY = y;
            this._pivotX = x * this.width;
            this._pivotY = y * this.height;
            this._transChanged(TransformKind.Anchor);
        }
        return this;
    }
    size(width, height) {
        let bw, bh;
        if (width == null) {
            bw = (this._sizeFlag & 1) != 0;
            this._sizeFlag &= ~1;
        }
        else {
            bw = this._width != width || (this._sizeFlag & 1) == 0;
            this._width = width;
            this._pivotX = this._anchorX * width;
            this._sizeFlag |= 1;
        }
        if (height == null) {
            bh = (this._sizeFlag & 2) != 0;
            this._sizeFlag &= ~2;
        }
        else {
            bh = this._height != height || (this._sizeFlag & 2) == 0;
            this._height = height;
            this._pivotY = this._anchorY * height;
            this._sizeFlag |= 2;
        }
        if (bw || bh)
            this._transChanged((bw ? TransformKind.Width : 0) | (bh ? TransformKind.Height : 0));
        return this;
    }
    scale(x, y) {
        if (this._scaleX !== x || this._scaleY !== y) {
            this._scaleX = x;
            this._scaleY = y;
            this._transChanged(TransformKind.Scale);
        }
        return this;
    }
    skew(x, y) {
        if (this._skewX !== x || this._skewY !== y) {
            this._skewX = x;
            this._skewY = y;
            this._transChanged(TransformKind.Skew);
        }
        return this;
    }
    _transChanged(kind) {
        var _a;
        if (this._destroyed)
            return;
        this.parentRepaint(SpriteConst.REPAINT_CACHE);
        if (kind != TransformKind.Pos && kind != TransformKind.Anchor) {
            this._tfChanged = true;
            this._renderType |= SpriteConst.TRANSFORM;
            if ((kind & TransformKind.Size) != 0)
                (_a = this._graphics) === null || _a === void 0 ? void 0 : _a._clearBoundsCache(true);
        }
        else {
            let p = this._cacheStyle.maskParent;
            if (p)
                p.repaint(SpriteConst.REPAINT_CACHE);
        }
        if ((kind & TransformKind.TRS) != 0) {
            if (this._globalTrans)
                this._globalTrans._spTransChanged(kind);
            if (this._getBit(NodeFlags.DEMAND_TRANS_EVENT))
                notifyTransChanged(this);
        }
    }
    render(ctx, x, y) {
        RenderSprite.renders[this._renderType]._fun(this, ctx, x + this._x, y + this._y);
        this._repaint = 0;
    }
    customRender(context, x, y) {
        this._repaint = SpriteConst.REPAINT_ALL;
    }
    drawToCanvas(canvasWidth, canvasHeight, offsetX, offsetY) {
        return Sprite.drawToCanvas(this, canvasWidth, canvasHeight, offsetX, offsetY);
    }
    static drawToCanvas(sprite, canvasWidth, canvasHeight, offsetX, offsetY, isDrawRenderRect = true) {
        let rt = Sprite.drawToRenderTexture2D(sprite, canvasWidth, canvasHeight, offsetX, offsetY, null);
        var dt = rt.getData(0, 0, canvasWidth, canvasHeight);
        var imgdata = new ImageData(canvasWidth, canvasHeight);
        ;
        var lineLen = canvasWidth * 4;
        var dst = imgdata.data;
        var y = canvasHeight - 1;
        var off = y * lineLen;
        var srcoff = 0;
        for (; y >= 0; y--) {
            dst.set(dt.subarray(srcoff, srcoff + lineLen), off);
            off -= lineLen;
            srcoff += lineLen;
        }
        var canv = new HTMLCanvas(true);
        canv.size(canvasWidth, canvasHeight);
        var ctx2d = canv.getContext('2d');
        ctx2d.putImageData(imgdata, 0, 0);
        rt.destroy();
        return canv;
    }
    drawToTexture(canvasWidth, canvasHeight, offsetX, offsetY, rt = null, isDrawRenderRect = true) {
        let res = Sprite.drawToTexture(this, canvasWidth, canvasHeight, offsetX, offsetY, rt, isDrawRenderRect);
        return res;
    }
    static drawToTexture(sprite, canvasWidth, canvasHeight, offsetX, offsetY, rt = null, isDrawRenderRect = true) {
        let renderout = rt || new RenderTexture2D(canvasWidth, canvasHeight, RenderTargetFormat.R8G8B8A8);
        let ctx = new Context();
        if (rt) {
            ctx.size(rt.width, rt.height);
        }
        else {
            ctx.size(canvasWidth, canvasHeight);
        }
        ctx.render2D = ctx.render2D.clone(null);
        let outrt = RenderSprite.RenderToRenderTexture(sprite, ctx, offsetX, offsetY, renderout, isDrawRenderRect);
        ctx._drawingToTexture = false;
        ctx.destroy();
        if (!rt) {
            let outTexture = new Texture(outrt, Texture.INV_UV);
            return outTexture;
        }
        return outrt;
    }
    drawToRenderTexture2D(canvasWidth, canvasHeight, offsetX, offsetY, rt = null, isDrawRenderRect = true, flipY = false) {
        let res = Sprite.drawToRenderTexture2D(this, canvasWidth, canvasHeight, offsetX, offsetY, rt, isDrawRenderRect, flipY);
        return res;
    }
    static drawToRenderTexture2D(sprite, canvasWidth, canvasHeight, offsetX, offsetY, rt = null, isDrawRenderRect = true, flipY = false) {
        let renderout = rt || new RenderTexture2D(canvasWidth, canvasHeight, RenderTargetFormat.R8G8B8A8);
        let ctx = new Context();
        if (rt) {
            ctx.size(rt.width, rt.height);
        }
        else {
            ctx.size(canvasWidth, canvasHeight);
        }
        ctx.render2D = ctx.render2D.clone(renderout);
        ctx._drawingToTexture = true;
        if (flipY) {
            renderout._invertY = true;
        }
        let outrt = RenderSprite.RenderToRenderTexture(sprite, ctx, offsetX, offsetY, renderout, isDrawRenderRect);
        ctx._drawingToTexture = false;
        ctx.destroy();
        return outrt;
    }
    hitTestPoint(x, y) {
        let point = this.globalToLocal(tmpPoint.setTo(x, y));
        x = point.x;
        y = point.y;
        var rect = this._hitArea ? this._hitArea :
            (this._isWidthSet && this._isHeightSet) ? tmpRect.setTo(0, 0, this._width, this._height) : this.getSelfBounds(tmpRect);
        return rect.contains(x, y, this);
    }
    setSelfBounds(bound) {
        this._userBounds = bound;
    }
    getBounds(out) {
        return Rectangle._getWrapRec(this._boundPointsToParent(), out);
    }
    getSelfBounds(out) {
        out = out || new Rectangle();
        if (this._userBounds)
            return out.copyFrom(this._userBounds);
        if (!this._graphics && this._children.length === 0 && !this._texture)
            return out.setTo(0, 0, this._width, this._height);
        else {
            tmpPoints.length = 0;
            return Rectangle._getWrapRec(this._getBoundPointsM(false, tmpPoints), out);
        }
    }
    getChildrenBounds(recursive, ignoreInvisibles, ignoreScale, out) {
        out = out || new Rectangle();
        out.setTo(0, 0, 0, 0);
        let children = recursive ? this._children : this._$children;
        for (let child of children) {
            if (ignoreInvisibles && !child._getBit(NodeFlags.ACTUAL_VISIBLE))
                continue;
            let w = child.width;
            let h = child.height;
            if (!ignoreScale) {
                w *= Math.abs(child._scaleX);
                h *= Math.abs(child._scaleY);
            }
            out.union(tmpRect2.setTo(child._x - w * child._anchorX, child._y - h * child._anchorY, w, h), out);
            if (recursive && child._children.length > 0) {
                let rect = child.getChildrenBounds(recursive, ignoreInvisibles, ignoreScale);
                rect.x += child._x;
                rect.y += child._y;
                out.union(rect, out);
            }
        }
        return out;
    }
    _boundPointsToParent(ifRotate) {
        let px = this._pivotX, py = this._pivotY;
        ifRotate = ifRotate || (this._rotation !== 0);
        if (this._scrollRect != null) {
            px += this._scrollRect.x;
            py += this._scrollRect.y;
        }
        let pts = this._tmpBounds || (this._tmpBounds = []);
        pts.length = 0;
        this._getBoundPointsM(ifRotate, pts);
        if (pts.length == 0)
            return pts;
        if (pts.length != 8) {
            if (ifRotate)
                GrahamScan.scanPList(pts);
            else {
                Rectangle._getWrapRec(pts, tmpRect2);
                pts.length = 0;
                tmpRect2.getBoundPoints(pts);
            }
        }
        if (!this.transform) {
            let len = pts.length;
            for (let i = 0; i < len; i += 2) {
                tmpPoint.x = pts[i];
                tmpPoint.y = pts[i + 1];
                this.toParentPoint(tmpPoint);
                pts[i] = tmpPoint.x;
                pts[i + 1] = tmpPoint.y;
            }
        }
        else {
            let dx = this._x - px;
            let dy = this._y - py;
            let len = pts.length;
            for (let i = 0; i < len; i += 2) {
                pts[i] += dx;
                pts[i + 1] += dy;
            }
        }
        return pts;
    }
    _getBoundPointsM(ifRotate, out) {
        var _a, _b;
        out = out || [];
        if (this._userBounds != null)
            return this._userBounds.getBoundPoints(out);
        if (this._scrollRect != null)
            return this._scrollRect.getBoundPoints(out);
        if (this._graphics != null)
            out.push(...this._graphics.getBoundPoints());
        if (this._renderNode != null || this._texture != null)
            tmpRect2.setTo(0, 0, this._width || ((_a = this._texture) === null || _a === void 0 ? void 0 : _a.width), this._height || ((_b = this._texture) === null || _b === void 0 ? void 0 : _b.height)).getBoundPoints(out);
        let chidren = this._children;
        for (let i = 0, n = chidren.length; i < n; i++) {
            let child = chidren[i];
            if (child._getBit(NodeFlags.ACTUAL_VISIBLE) && child._cacheStyle.maskParent != this) {
                out.push(...child._boundPointsToParent(ifRotate));
            }
        }
        return out;
    }
    getGraphicBounds(realSize, out) {
        out = out || new Rectangle();
        if (this._graphics)
            return out.copyFrom(this._graphics.getBounds(realSize));
        else
            return out.setTo(0, 0, 0, 0);
    }
    localToGlobal(point, createNewPoint, globalNode) {
        if (createNewPoint) {
            point = new Point(point.x, point.y);
        }
        var ele = this;
        globalNode = globalNode || ILaya.stage;
        while (ele && !ele._destroyed) {
            if (ele == globalNode)
                break;
            point = ele.toParentPoint(point);
            ele = ele._parent;
        }
        return point;
    }
    globalToLocal(point, createNewPoint, globalNode) {
        if (createNewPoint) {
            point = new Point(point.x, point.y);
        }
        var ele = this;
        var list = [];
        globalNode = globalNode || ILaya.stage;
        while (ele && !ele._destroyed) {
            if (ele == globalNode)
                break;
            list.push(ele);
            ele = ele._parent;
        }
        var i = list.length - 1;
        while (i >= 0) {
            ele = list[i];
            point = ele.fromParentPoint(point);
            i--;
        }
        return point;
    }
    toParentPoint(point) {
        if (!point)
            return point;
        point.x -= this.pivotX;
        point.y -= this.pivotY;
        if (this.transform)
            this._transform.transformPoint(point);
        point.x += this._x;
        point.y += this._y;
        var scroll = this._scrollRect;
        if (scroll) {
            point.x -= scroll.x * this._scaleX;
            point.y -= scroll.y * this._scaleY;
        }
        return point;
    }
    fromParentPoint(point) {
        if (!point)
            return point;
        point.x -= this._x;
        point.y -= this._y;
        var scroll = this._scrollRect;
        if (scroll) {
            point.x += scroll.x * this._scaleX;
            point.y += scroll.y * this._scaleY;
        }
        if (this.transform)
            this._transform.invertTransformPoint(point);
        point.x += this.pivotX;
        point.y += this.pivotY;
        return point;
    }
    loadImage(url, complete) {
        if (!url) {
            this.texture = null;
            this.repaint(SpriteConst.REPAINT_ALL);
            complete && complete.run();
        }
        else {
            let tex = ILaya.loader.getRes(url);
            if (tex) {
                this.texture = tex;
                this.repaint(SpriteConst.REPAINT_ALL);
                complete && complete.run();
            }
            else {
                if (this._skinBaseUrl)
                    url = URL.formatURL(url, this._skinBaseUrl);
                ILaya.loader.load(url).then((tex) => {
                    this.texture = tex;
                    this.repaint(SpriteConst.REPAINT_ALL);
                    complete && complete.run();
                });
            }
        }
        return this;
    }
    static fromImage(url) {
        return new Sprite().loadImage(url);
    }
    _getCacheStyle() {
        this._cacheStyle === CacheStyle.EMPTY && (this._cacheStyle = CacheStyle.create());
        return this._cacheStyle;
    }
    reCache() {
        this._repaint |= SpriteConst.REPAINT_CACHE;
    }
    getRepaint() {
        return this._repaint;
    }
    repaint(type = SpriteConst.REPAINT_CACHE) {
        if (!(this._repaint & type)) {
            this._repaint |= type;
            this.parentRepaint(type);
        }
        if (this._cacheStyle) {
            this._cacheStyle.renderTexture = null;
            if (this._cacheStyle.maskParent)
                this._cacheStyle.maskParent.repaint(type);
        }
    }
    _needRepaint() {
        return !!(this._repaint & SpriteConst.REPAINT_CACHE);
    }
    parentRepaint(type = SpriteConst.REPAINT_CACHE) {
        var p = this._parent;
        if (p && !(p._repaint & type)) {
            p._repaint |= type;
            p.parentRepaint(type);
        }
    }
    get dragSupport() {
        return this._dragSupport || (this._dragSupport = new DragSupport(this));
    }
    startDrag(area, hasInertia, elasticDistance, elasticBackTime, data, ratio) {
        let d = this.dragSupport;
        area != null && d.area.copyFrom(area);
        hasInertia != null && (d.hasInertia = hasInertia);
        elasticDistance != null && (d.elasticDistance = elasticDistance);
        elasticBackTime != null && (d.elasticBackTime = elasticBackTime);
        ratio != null && (d.ratio = ratio);
        d.start(data);
    }
    stopDrag() {
        this._dragSupport && this._dragSupport.stop();
    }
    onAfterDeserialize() {
        super.onAfterDeserialize();
        if (LayaEnv.isPlaying) {
            if (this._gcmds) {
                this.graphics.cmds = this._gcmds;
                delete this._gcmds;
            }
            if (this._filters) {
                this.filters = this._filters;
                delete this._filters;
            }
        }
    }
    onStartListeningToType(type) {
        super.onStartListeningToType(type);
        if (Event.isMouseEvent(type)) {
            if (this._mouseState === 0) {
                this._setBit(NodeFlags.CHECK_INPUT, true);
                this.setMouseEnabledUp();
            }
        }
        else if (type === Event.TRANSFORM_CHANGED) {
            this._setBit(NodeFlags.DEMAND_TRANS_EVENT, true);
            this.setDemandTransEventUp();
        }
    }
    setDemandTransEventUp() {
        let p = this._parent;
        while (p && p !== ILaya.stage) {
            if (!p._setBit(NodeFlags.DEMAND_TRANS_EVENT, true))
                break;
            p = p._parent;
        }
    }
    _setDisplay(value) {
        if (!value && this._cacheStyle)
            this._cacheStyle.onInvisible();
        super._setDisplay(value);
    }
    _processVisible() {
        if (this._setBit(NodeFlags.ACTUAL_VISIBLE, this._visible && !this._getBit(hiddenBits) || this._getBit(NodeFlags.FORCE_VISIBLE))) {
            this.parentRepaint(SpriteConst.REPAINT_ALL);
            return true;
        }
        else
            return false;
    }
    _setParent(value) {
        super._setParent(value);
        if (value && (this._mouseState === 2 || this._mouseState === 0 && this._getBit(NodeFlags.CHECK_INPUT))
            && !value._getBit(NodeFlags.CHECK_INPUT)) {
            this.setMouseEnabledUp();
        }
        if (value && this._getBit(NodeFlags.DEMAND_TRANS_EVENT) && !value._getBit(NodeFlags.DEMAND_TRANS_EVENT))
            this.setDemandTransEventUp();
    }
    _childChanged(child) {
        super._childChanged(child);
        if (this._children.length)
            this._renderType |= SpriteConst.CHILDS;
        else
            this._renderType &= ~SpriteConst.CHILDS;
        if (child) {
            if (child._zOrder)
                this._setBit(NodeFlags.HAS_ZORDER, true);
        }
        if (this._getBit(NodeFlags.HAS_ZORDER))
            ILaya.systemTimer.callLater(this, this.updateZOrder);
        this.repaint(SpriteConst.REPAINT_ALL);
    }
    _addComponentInstance(comp) {
        var _a;
        if (comp instanceof BaseRenderNode2D &&
            ((_a = this._components) === null || _a === void 0 ? void 0 : _a.some((c) => c instanceof BaseRenderNode2D))) {
            console.warn(`${this.name} add RenderNode2D invalid, one sprite can only add one RenderNode`);
            return;
        }
        super._addComponentInstance(comp);
    }
}
const tmpRect = new Rectangle();
const tmpRect2 = new Rectangle();
const tmpPoint = new Point();
const tmpPoints = [];
function notifyTransChanged(sp) {
    sp.event(Event.TRANSFORM_CHANGED);
    for (let child of sp._children) {
        if (child._getBit(NodeFlags.DEMAND_TRANS_EVENT))
            notifyTransChanged(child);
    }
}
